import CandleStickViz from '../vizs/CandleStickViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 *
 * Used to display price movements of a security, derivative, or currency.
 *
 * Each "candlestick" typically shows one day, thus a one-month chart may show the 20 trading days as 20 candlesticks. Candlestick charts can also be built using intervals shorter or longer than one day. It is similar to a bar chart in that each candlestick represents all four important pieces of information for that day: open and close in the thick body; high and low in the “candle wick”.
 *
 * ### Example Usage
 * ```javascript
 *   import {CandleStick} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new CandleStick($el, data, config);
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 5 columns. Each row represents an entry, and the first column should be the X value, then its open,close, high and low values.
 *
 * | X       |     Open  |    Close  |  High   |   Low    |
 * | -------- | ------ |-------|----------|-----------|
 * |   1      |    22.4|   22.4|  15.66  |   12.30    |
 * |   2      |    52.3|   12.5|   52.4 |   32.45    |
 * |   3      |    12.8|   52.2|  48.10  |   44.10    |
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 *
 * Se utiliza para mostrar movimientos de precios de un valor, derivado o moneda.
 *
 * Cada "vela" normalmente muestra un día, por lo que un gráfico de un mes puede mostrar los 20 días de negociación como 20 velas. Los gráficos de velas japonesas también se pueden crear utilizando intervalos más cortos o más largos que un día. Es similar a un gráfico de barras en el sentido de que cada vela representa los cuatro datos importantes de ese día: apertura y cierre en el cuerpo grueso; alto y bajo en la “mecha de vela”.
 *
 * ### Ejemplo de uso
 * ```javascript
 *   import {CandleStick} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new CandleStick($el, data, config);
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 5 columnas. Cada fila representa una entrada, y la primera columna debe ser el valor X, luego sus valores de apertura, cierre, máximo y mínimo.
 *
 * | X       |     Apertura  |    Cierre  |  Máximo   |   Mínimo    |
 * | -------- | ------ |-------|----------|-----------|
 * |   1      |    22.4|   22.4|  15.66  |   12.30    |
 * |   2      |    52.3|   12.5|   52.4 |   32.45    |
 * |   3      |    12.8|   52.2|  48.10  |   44.10    |
 *
 * {.data-table}
 */
export default class CandleStickComponent extends BaseComponent {
  static vizClass = CandleStickViz
  static dataFormat = [
    {
      index: 0,
      axis: 'x0',
      label: 'id',
      types: [Number, Date],
      role: 'id',
      roleLabel: 'X value - Number or Date (dd-mm-yyyy)',
    },
    { index: 1, axis: 'y0', label: 'Open', types: [Number], role: 'value', roleLabel: 'Open' },
    { index: 2, axis: 'y0', label: 'Close', types: [Number], role: 'value', roleLabel: 'Close' },
    { index: 3, axis: 'y0', label: 'High', types: [Number], role: 'value', roleLabel: 'High' },
    { index: 4, axis: 'y0', label: 'Low', types: [Number], role: 'value', roleLabel: 'Low' },
  ]
  static componentLabel = 'Candlestick'
  static componentLabelEs = 'Gráfico de velas'
  static defaultConfig = {
    title: this.componentLabel,
    TooltipBehavior: {
      tooltipValueFormat: {
        exposed: true,
        nonEditable: false,
      },
    },
  }
  static dataRequirements = {
    recomendedMaxRows: 20,
    maxRows: 30,
    requirementsStrength: 20,
    customMultiplier: rows => {
      return rows.reduce((mult, r) => {
        const [id, open, close, high, low] = r.map(v => Number(v))
        let complies =
          high >= open && high >= close && high >= low && low <= open && low <= close && low <= high
        if (complies) return mult * 1
        else return mult * 0
      }, 2)
    },
  }
}
